// Požadavky na svoz (F4 4.8; kontrakt tag Zakazky, 08 §2 `svoz_pozadavky`, akce `svoz_pozadavek.zalozit` /
// `svoz_pozadavek.planovat`):
//   GET    /pozadavky-svozu?stav&zakaznik_id&jen_moje&specha&hledat   stránkovaný seznam; `ceka` = počet čekajících bez
//                                                                     ohledu na stránku a filtr (dlaždice, menu)
//   GET    /pozadavky-svozu/{id}                                      karta požadavku
//   POST   /pozadavky-svozu                                           založit (`PozadavekSvozuVstup`: `co` povinné; ODKUD = naše
//                                                                     zařízení | zákazník (+ provozovna) | nové místo s názvem,
//                                                                     adresou a kontaktem — jinak 400 s polem)
//   PUT    /pozadavky-svozu/{id}, DELETE …                            jen vlastní požadavek ve stavu `ceka` (jinak 409)
//   POST   /pozadavky-svozu/{id}/naplanovat                           zůstává v API (asistent, zpětná kompatibilita); obrazovka
//                                                                     od W27 zakládá zakázku přes `…/zakazka` (Nová zakázka)
//   GET    /pozadavky-svozu/caste-polozky?zakaznik_id&provozovna_id&zarizeni_id   co se u místa vozí nejčastěji (rychlé
//                                                                     volby „Co odvézt“ žadatele, W27) — bez cen
//   GET    /pozadavky-svozu/{id}/navrh-zakazky                        předvyplnění Nové zakázky a návrh položek (W27)
//   POST   /pozadavky-svozu/{id}/zakazka                              zakázka z formuláře Nové zakázky + položky (W27);
//                                                                     datum, řidič a vozidlo nepovinné
//   POST   /pozadavky-svozu/{id}/zamitnout { duvod }                  důvod povinný — žadatel ho uvidí
//   POST   /pozadavky-svozu/{id}/zrusit                               žadatel (nebo plánující role) zruší čekající požadavek
//   POST   /pozadavky-svozu/{id}/predat { resitel_id, poznamka }      předat jinému řešiteli (W41)
//   GET    /pozadavky-svozu/resitele                                  kdo může požadavek vyřešit a kdo je výchozí (W41)
//   PUT    /pozadavky-svozu/vychozi-resitel { resitel_id }            výchozí řešitel nových požadavků (administrátor)
// Řádková viditelnost řeší server: žadatel vidí své, A J D K všechny. Přechody stavů jen přes akce; UI kreslí tlačítka
// podle `muze` s důvody. Asistent operace jen navrhuje k potvrzení (`?nahled=true`).
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';
import { KLIC_ZAKAZKY } from './klice-zakazky';
import type { ZakazkaKarta } from './zakazky';

/** Karta požadavku; `id` server posílá vždy. */
export type PozadavekSvozu = Schema<'PozadavekSvozu'> & { id: string };
export type PozadavekSvozuSeznam = Omit<Schema<'PozadavekSvozuSeznam'>, 'polozky'> & {
  polozky: PozadavekSvozu[];
};
export type PozadavekSvozuVstup = Schema<'PozadavekSvozuVstup'>;
export type StavPozadavku = PozadavekSvozu['stav'];
export type RadekPozadavku = Schema<'PozadavekSvozuPolozka'>;
export type RadekPozadavkuVstup = Schema<'PozadavekSvozuPolozkaVstup'>;
export type CastaPolozka = Schema<'PozadavekSvozuCastaPolozka'>;
export type NavrhZakazky = Schema<'NavrhZakazkyZPozadavku'>;
export type NavrhPolozky = Schema<'NavrhPolozkyZPozadavku'>;
export type ZakazkaZPozadavkuVstup = Schema<'ZakazkaZPozadavkuVstup'>;
export type ResitelePozadavku = Schema<'PozadavekSvozuResitele'>;
export type ResitelPozadavku = Schema<'PozadavekSvozuResitel'>;

export const AKCE_ZALOZIT_SVOZ = 'svoz_pozadavek.zalozit';
export const AKCE_PLANOVAT_SVOZ = 'svoz_pozadavek.planovat';

export const KLIC_SVOZ = ['svoz-pozadavky'] as const;
export const VELIKOST_SEZNAMU_SVOZU = 100;

/** Stavy požadavku (02 `pozadavek_svozu.stav`): popisek podle `stav_popis` serveru a štítek podle 04. */
export const STAVY_POZADAVKU: Readonly<Record<StavPozadavku, { popisek: string; stitek: string }>> =
  {
    ceka: { popisek: 'Čeká na dispečera', stitek: 'stitek--warn' },
    naplanovano: { popisek: 'Naplánováno', stitek: 'stitek--ok' },
    zamitnuto: { popisek: 'Zamítnuto', stitek: 'stitek--danger' },
    zruseno: { popisek: 'Zrušeno žadatelem', stitek: 'stitek--neutral' },
  };

export const STAVY_POZADAVKU_PORADI: readonly StavPozadavku[] = [
  'ceka',
  'naplanovano',
  'zamitnuto',
  'zruseno',
];

export function popisStavuPozadavku(p: Pick<PozadavekSvozu, 'stav' | 'stav_popis'>): string {
  return p.stav_popis || STAVY_POZADAVKU[p.stav]?.popisek || p.stav;
}

export function stitekStavuPozadavku(stav: string): string {
  return STAVY_POZADAVKU[stav as StavPozadavku]?.stitek ?? 'stitek--neutral';
}

export interface FiltrPozadavku {
  stav?: StavPozadavku | '';
  jenMoje?: boolean;
  specha?: boolean;
  zakaznikId?: string;
  /** Filtr „Řeší“ (W41). */
  resitelId?: string;
  /** „Moje k vyřízení“ (W41): čekající, které mám vyřešit já. */
  kVyrizeni?: boolean;
  hledat?: string;
  /** Strana seznamu (od 1); bez ní první. */
  strana?: number;
}

export function dotazPozadavku(filtr: FiltrPozadavku): string {
  const q = new URLSearchParams({
    strana: String(filtr.strana ?? 1),
    velikost: String(VELIKOST_SEZNAMU_SVOZU),
    razeni: 'vytvoreno:desc',
  });
  if (filtr.stav) q.set('stav', filtr.stav);
  if (filtr.jenMoje) q.set('jen_moje', 'true');
  if (filtr.specha) q.set('specha', 'true');
  if (filtr.zakaznikId) q.set('zakaznik_id', filtr.zakaznikId);
  if (filtr.resitelId) q.set('resitel_id', filtr.resitelId);
  if (filtr.kVyrizeni) q.set('k_vyrizeni', 'true');
  if (filtr.hledat?.trim()) q.set('hledat', filtr.hledat.trim());
  return `?${q.toString()}`;
}

export const klicSeznamuPozadavku = (filtr: FiltrPozadavku = {}) =>
  [...KLIC_SVOZ, 'seznam', filtr] as const;

export function nacistPozadavky(
  filtr: FiltrPozadavku,
  signal?: AbortSignal,
): Promise<PozadavekSvozuSeznam> {
  return api.get<PozadavekSvozuSeznam>(`/pozadavky-svozu${dotazPozadavku(filtr)}`, { signal });
}

export function useSvozPozadavky(filtr: FiltrPozadavku, volby: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: klicSeznamuPozadavku(filtr),
    queryFn: ({ signal }) => nacistPozadavky(filtr, signal),
    placeholderData: keepPreviousData,
    enabled: volby.enabled ?? true,
  });
}

/** Klíč počtu čekajících v menu — sdílí prefix se seznamem, po každém zápisu se přepočítá. */
export const KLIC_POCTU_SVOZU = [...KLIC_SVOZ, 'seznam', 'pocet-ceka'] as const;

/** Počet čekajících požadavků pro menu „Požadavky na svoz (n)“ (`PozadavekSvozuSeznam.ceka`, jeden řádek stačí). */
export function usePocetSvozuCeka(povoleno: boolean) {
  return useQuery({
    queryKey: KLIC_POCTU_SVOZU,
    queryFn: ({ signal }) =>
      api.get<PozadavekSvozuSeznam>('/pozadavky-svozu?stav=ceka&velikost=1', { signal }),
    select: (data) => data.ceka ?? data.celkem,
    enabled: povoleno,
    staleTime: 30_000,
  });
}

/** Kdo může požadavek vyřešit a kdo je výchozí (W41) — „Kdo to vyřeší“, „Předat jinému“, filtr „Řeší“. */
export function useResitelePozadavku(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_SVOZ, 'resitele'],
    queryFn: ({ signal }) => api.get<ResitelePozadavku>('/pozadavky-svozu/resitele', { signal }),
    enabled: povoleno,
    staleTime: 60_000,
  });
}

export function usePozadavekSvozu(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_SVOZ, 'karta', id],
    queryFn: ({ signal }) =>
      api.get<PozadavekSvozu>(`/pozadavky-svozu/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: Boolean(id),
  });
}

/** Po každém zápisu se načte znovu seznam, počet v menu a (po naplánování) seznamy zakázek. */
export function obnovitPozadavky(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: KLIC_SVOZ });
}

const cesta = (id: string, akce = '') =>
  `/pozadavky-svozu/${encodeURIComponent(id)}${akce ? `/${akce}` : ''}`;

export const zalozitPozadavek = (telo: PozadavekSvozuVstup) =>
  api.post<PozadavekSvozu>('/pozadavky-svozu', telo);

export const upravitPozadavek = (id: string, telo: PozadavekSvozuVstup) =>
  api.put<PozadavekSvozu>(cesta(id), telo);

export const smazatPozadavek = (id: string) => api.delete<void>(cesta(id));

/** Co se u místa vozí nejčastěji (rychlé volby žadatele); bez místa se server neptá. */
export function useCastePolozky(misto: {
  zakaznikId?: string;
  provozovnaId?: string;
  zarizeniId?: string;
}) {
  const q = new URLSearchParams();
  if (misto.zakaznikId) q.set('zakaznik_id', misto.zakaznikId);
  if (misto.provozovnaId) q.set('provozovna_id', misto.provozovnaId);
  if (misto.zarizeniId) q.set('zarizeni_id', misto.zarizeniId);
  const dotaz = q.toString();
  return useQuery({
    queryKey: [...KLIC_SVOZ, 'caste-polozky', dotaz],
    queryFn: ({ signal }) =>
      api.get<{ polozky: CastaPolozka[] }>(`/pozadavky-svozu/caste-polozky?${dotaz}`, { signal }),
    enabled: dotaz !== '',
    staleTime: 5 * 60_000,
  });
}

/** Návrh zakázky z požadavku (předvyplnění formuláře Nové zakázky) — jen dispečer a vyšší role. */
export function useNavrhZakazky(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_SVOZ, 'navrh-zakazky', id],
    queryFn: ({ signal }) => api.get<NavrhZakazky>(cesta(id ?? '', 'navrh-zakazky'), { signal }),
    enabled: Boolean(id),
    retry: false,
    // Návrh se čte jednou při otevření formuláře — dispečer ho pak upravuje sám.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Vytvořit zakázku z požadavku — server založí zakázku s položkami a požadavek vyřídí. */
export async function vytvoritZakazkuZPozadavku(
  queryClient: QueryClient,
  id: string,
  telo: ZakazkaZPozadavkuVstup,
): Promise<ZakazkaKarta> {
  const karta = await api.post<ZakazkaKarta>(cesta(id, 'zakazka'), telo);
  queryClient.setQueryData([...KLIC_ZAKAZKY, 'karta', karta.id], karta);
  void queryClient.invalidateQueries({ queryKey: [...KLIC_ZAKAZKY, 'seznam'] });
  obnovitPozadavky(queryClient);
  return karta;
}

export const zamitnoutPozadavek = (id: string, duvod: string) =>
  api.post<PozadavekSvozu>(cesta(id, 'zamitnout'), { duvod });

export const zrusitPozadavek = (id: string) => api.post<PozadavekSvozu>(cesta(id, 'zrusit'));

// --- Formulář požadavku (sdílený mezi sekcí, Můj den a Domů) ------------------------------------------------------

/** Odkud se odváží: naše zařízení | evidovaný zákazník (+ provozovna) | jiné místo, které v systému není. */
export type OdkudTyp = 'zarizeni' | 'zakaznik' | 'jine';

export const ODKUD_TYPY: readonly { klic: OdkudTyp; nazev: string }[] = [
  { klic: 'zarizeni', nazev: 'Naše zařízení' },
  { klic: 'zakaznik', nazev: 'Zákazník' },
  { klic: 'jine', nazev: 'Jiné místo / nový zákazník' },
];

export interface FormularPozadavku {
  odkudTyp: OdkudTyp;
  odkudZarizeniId: string;
  odkudZakaznikId: string;
  odkudProvozovnaId: string;
  odkudNazev: string;
  odkudAdresa: string;
  kontakt: string;
  co: string;
  termin: string;
  kam: string;
  kamZakaznikId: string;
  specha: boolean;
  /** Druh nádoby (05/2255); prázdné = žadatel neuvedl. */
  nadoba: DruhNadoby | '';
  /** Přistavit prázdný kontejner (05/2255). */
  pristavitPrazdny: boolean;
  /** Rychlé volby „Co odvézt“ (W27): položka katalogu, množství a jednotka (prázdné = žadatel neví). */
  volby: VolbaFormulare[];
  /** „Kdo to vyřeší“ (W41) — jen při založení; prázdné = výchozí řešitel (koordinátor dopravy). */
  resitelId: string;
}

/** Druh nádoby s odpadem (rozhodnutí zadavatele 5. 10. 2026, 05/2255). */
export type DruhNadoby = 'kontejner' | 'big_bag' | 'paleta';
export const DRUHY_NADOB: ReadonlyArray<{ klic: DruhNadoby; nazev: string }> = [
  { klic: 'kontejner', nazev: 'Kontejner' },
  { klic: 'big_bag', nazev: 'Big bag' },
  { klic: 'paleta', nazev: 'Paleta' },
];

/** „Big bag · přistavit prázdný kontejner“ pro seznam a pruh dispečera; null = nic neuvedeno. */
export function popisNadoby(p: {
  nadoba?: string | null;
  pristavit_prazdny?: boolean;
}): string | null {
  const casti = [
    DRUHY_NADOB.find((d) => d.klic === p.nadoba)?.nazev ?? null,
    p.pristavit_prazdny ? 'přistavit prázdný kontejner' : null,
  ].filter((c): c is string => c !== null);
  return casti.length > 0 ? casti.join(' · ') : null;
}

/** Rychlá volba ve formuláři: položka z nabídky místa s nepovinným množstvím. */
export interface VolbaFormulare {
  polozka_id: string;
  kod?: string;
  nazev: string;
  mnozstvi: string;
  mj: string;
}

export const FORMULAR_POZADAVKU_PRAZDNY: FormularPozadavku = {
  odkudTyp: 'zakaznik',
  odkudZarizeniId: '',
  odkudZakaznikId: '',
  odkudProvozovnaId: '',
  odkudNazev: '',
  odkudAdresa: '',
  kontakt: '',
  co: '',
  termin: '',
  kam: '',
  kamZakaznikId: '',
  specha: false,
  nadoba: '',
  pristavitPrazdny: false,
  volby: [],
  resitelId: '',
};

/** Formulář z uloženého požadavku (úprava vlastního čekajícího). */
export function formularZPozadavku(p: PozadavekSvozu): FormularPozadavku {
  return {
    odkudTyp: p.nove_misto ? 'jine' : p.odkud_zarizeni_id ? 'zarizeni' : 'zakaznik',
    odkudZarizeniId: p.odkud_zarizeni_id ?? '',
    odkudZakaznikId: p.odkud_zakaznik_id ?? '',
    odkudProvozovnaId: p.odkud_provozovna_id ?? '',
    odkudNazev: p.nove_misto ? p.odkud_nazev : '',
    odkudAdresa: p.nove_misto ? p.odkud_adresa : '',
    kontakt: p.kontakt ?? '',
    co: p.co,
    termin: p.termin ?? '',
    kam: p.kam ?? '',
    kamZakaznikId: p.kam_zakaznik_id ?? '',
    specha: p.specha,
    nadoba: p.nadoba ?? '',
    pristavitPrazdny: p.pristavit_prazdny,
    // Text složený z rychlých voleb (žadatel nic nenapsal) se v úpravě ukáže prázdný — volby jsou vidět samy.
    ...(p.co_souhrn ? { co: '' } : {}),
    volby: (p.polozky ?? []).map((r) => ({
      polozka_id: r.polozka_id,
      kod: r.kod,
      nazev: r.nazev,
      mnozstvi: r.mnozstvi !== undefined ? String(r.mnozstvi).replace('.', ',') : '',
      mj: r.mj ?? r.mj_katalog,
    })),
    // Řešitele úprava nemění (PUT ho ignoruje) — předává se akcí „Předat jinému“.
    resitelId: p.resitel_id ?? '',
  };
}

/** Množství z pole formuláře („2,5“) — prázdné nebo nesmyslné = žadatel neví (null). */
export function mnozstviZPole(text: string): number | null {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!t || !/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return n > 0 ? n : null;
}

/**
 * První věc, která formuláři chybí: text důvodu a (je-li) pole, které se po pokusu o odeslání zvýrazní. Pole nese
 * název z chyb serveru (`detaily[].pole`), aby ho formulář ukázal stejně jako odpověď serveru.
 */
export function prvniChybejiciPozadavek(
  f: FormularPozadavku,
): { pole?: string; zprava: string } | undefined {
  if (f.odkudTyp === 'zarizeni' && !f.odkudZarizeniId)
    return { pole: 'odkud_zarizeni_id', zprava: 'Vyberte naše zařízení, odkud se má odvážet.' };
  if (f.odkudTyp === 'zakaznik' && !f.odkudZakaznikId)
    return { pole: 'odkud_zakaznik_id', zprava: 'Vyberte zákazníka, odkud se má odvážet.' };
  if (f.odkudTyp === 'jine' && !f.odkudNazev.trim())
    return { pole: 'odkud_nazev', zprava: 'Doplňte firmu / jméno místa, které v systému není.' };
  if (f.odkudTyp === 'jine' && !f.odkudAdresa.trim())
    return { pole: 'odkud_adresa', zprava: 'Doplňte adresu místa, ať řidič ví, kam jet.' };
  // Stačí text, nebo aspoň jedna rychlá volba (W27).
  if (!f.co.trim() && f.volby.length === 0)
    return { pole: 'co', zprava: 'Napište, co a kolik se má odvézt — nebo vyberte z nabídky.' };
  const spatne = f.volby.find(
    (v) => v.mnozstvi.trim() !== '' && mnozstviZPole(v.mnozstvi) === null,
  );
  if (spatne) return { zprava: `Množství u „${spatne.nazev}“ musí být číslo větší než nula.` };
  return undefined;
}

/** Proč formulář zatím nejde odeslat (text vedle neaktivního tlačítka); undefined = jde. */
export function duvodFormularePozadavku(f: FormularPozadavku): string | undefined {
  return prvniChybejiciPozadavek(f)?.zprava;
}

/** Tělo POST / PUT z formuláře (jen pole zvolené varianty ODKUD). */
export function vstupZFormularePozadavku(f: FormularPozadavku): PozadavekSvozuVstup {
  const telo: PozadavekSvozuVstup = {
    co: f.co.trim(),
    polozky: f.volby.map((v): RadekPozadavkuVstup => {
      const mnozstvi = mnozstviZPole(v.mnozstvi);
      return mnozstvi === null
        ? { polozka_id: v.polozka_id }
        : { polozka_id: v.polozka_id, mnozstvi, mj: v.mj };
    }),
    specha: f.specha,
    nadoba: f.nadoba || null,
    pristavit_prazdny: f.pristavitPrazdny,
    termin: f.termin || undefined,
    kam: f.kam.trim() || undefined,
    kam_zakaznik_id: f.kamZakaznikId || undefined,
    resitel_id: f.resitelId || undefined,
  };
  if (f.odkudTyp === 'zarizeni') {
    telo.odkud_zarizeni_id = f.odkudZarizeniId;
  } else if (f.odkudTyp === 'zakaznik') {
    telo.odkud_zakaznik_id = f.odkudZakaznikId;
    telo.odkud_provozovna_id = f.odkudProvozovnaId || undefined;
  } else {
    telo.nove_misto = true;
    telo.odkud_nazev = f.odkudNazev.trim();
    telo.odkud_adresa = f.odkudAdresa.trim();
    telo.kontakt = f.kontakt.trim() || undefined;
  }
  return telo;
}

/** Předat požadavek jinému řešiteli s poznámkou (W41). */
export const predatPozadavek = (id: string, telo: { resitel_id: string; poznamka?: string }) =>
  api.post<PozadavekSvozu>(cesta(id, 'predat'), telo);

/** Výchozí řešitel nových požadavků (W41; administrátor — `nastaveni.firma`); null = koordinátor dopravy. */
export const nastavitVychozihoResitele = (resitelId: string | null) =>
  api.put<{ vychozi_id: string | null; nastaveny_id: string | null }>(
    '/pozadavky-svozu/vychozi-resitel',
    { resitel_id: resitelId },
  );
